import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { apiClient } from '@/lib/api-client';

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

const NotificationItemSchema = z.object({
  id: z.string(),
  eventType: z.string(),
  channel: z.string(),
  body: z.string(),
  subject: z.string().nullable(),
  isRead: z.boolean(),
  status: z.string(),
  createdAt: z.string(),
  sentAt: z.string().nullable().optional(),
});

export type NotificationItem = z.infer<typeof NotificationItemSchema>;

const NotificationsPageSchema = z.object({
  items: z.array(NotificationItemSchema),
  nextCursor: z.string().nullable(),
});

const UnreadCountSchema = z.object({ count: z.number() });

const MutationResultSchema = z.object({ updated: z.number() });

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useMyNotifications(cursor?: string) {
  const path = cursor
    ? `/notifications?limit=20&cursor=${encodeURIComponent(cursor)}`
    : '/notifications?limit=20';
  return useQuery({
    queryKey: ['notifications', cursor ?? ''],
    queryFn: () => apiClient.get(path, NotificationsPageSchema),
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => apiClient.get('/notifications/unread-count', UnreadCountSchema),
    refetchInterval: 30_000,
  });
}

export function useMarkRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      apiClient.post('/notifications/mark-read', { ids }, MutationResultSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post('/notifications/mark-all-read', {}, MutationResultSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Preferences
// ---------------------------------------------------------------------------

const NotificationPreferenceSchema = z.object({
  id: z.string(),
  eventType: z.string(),
  channel: z.enum(['in_app', 'email', 'sms', 'push']),
  isEnabled: z.boolean(),
});

export type NotificationPreference = z.infer<typeof NotificationPreferenceSchema>;

const PreferencesResponseSchema = z.object({
  preferences: z.array(NotificationPreferenceSchema),
});

const UpdatePreferenceResponseSchema = z.object({
  preference: NotificationPreferenceSchema,
});

export function useNotificationPreferences() {
  return useQuery({
    queryKey: ['notifications', 'preferences'],
    queryFn: () => apiClient.get('/notifications/preferences', PreferencesResponseSchema),
  });
}

export function useUpdateNotificationPreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { eventType: string; channel: string; isEnabled: boolean }) =>
      apiClient.put('/notifications/preferences', data, UpdatePreferenceResponseSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications', 'preferences'] });
    },
  });
}
